import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { User } from '../../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: User['role'] | User['role'][];
  redirectTo?: string;
}

/**
 * Protected Route - Only checks authentication
 *
 * NOTE: All users are models and all models are users.
 * The requiredRole prop is kept for backwards compatibility but is IGNORED.
 * All authenticated users have access to all features.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole: _requiredRole, // Kept for backwards compatibility but ignored - all users are models
  redirectTo = '/login',
}) => {
  // Suppress unused variable warning - kept for API compatibility
  void _requiredRole;

  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const location = useLocation();

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // All authenticated users have access to all features
  // No role-based restrictions - everyone is both a user AND a model
  return <>{children}</>;
};

export default ProtectedRoute;
